import { useMutation, useApolloClient } from '@apollo/client';
import { print } from 'graphql';
import { useCallback, useMemo, useEffect, useRef } from 'react';

import { useOfflineQueue } from './OfflineContext';
import offlineOperationConfigs from './config';
import { useAppContext } from '../../common/utils/context';

const isNetworkError = error => Boolean(error?.networkError) || (
	typeof error?.message === 'string' && [
		'network',
		'offline',
		'failed to fetch',
	].some(part => error.message.toLowerCase().includes(part))
);

/**
 * Extract the operation name from a GraphQL document node.
 *
 * @param {DocumentNode} documentNode - GraphQL document
 * @param {string} fallback - Fallback name if extraction fails
 * @returns {string} The operation name
 */
const getOperationName = (documentNode, fallback) => {
	if (!documentNode?.definitions) {
		return fallback;
	}

	const operationDefinition = documentNode.definitions.find(definition => definition.kind === 'OperationDefinition');

	if (operationDefinition?.name?.value) {
		return operationDefinition.name.value;
	}

	return fallback;
};

/**
 * A hook that wraps useMutation with offline queue support.
 *
 * When online:
 * - Behaves like a normal useMutation
 * - Mutation is executed immediately against the server
 *
 * When offline:
 * - Mutation is queued for later execution
 * - Optimistic updates are applied to Apollo cache (if configured)
 * - Returns an offline result to the caller
 * - Mutation will be retried when connectivity is restored
 *
 * Operation configuration:
 * - Pass a configKey to use a predefined operation config
 * - Or pass offlineOverride to provide custom offline behavior
 * - forceQueue keeps a new write behind earlier queued writes to the same record
 * - Configs define serialization, attachment handling, optimistic updates, etc.
 *
 * @param {DocumentNode} documentNode - GraphQL mutation document
 * @param {Object} options - Options including configKey and mutation options
 * @returns {[Function, Object]} Tuple of [mutate function, mutation state]
 */
const useOfflineMutation = (documentNode, options = {}) => {
	const {
		configKey,
		offlineOverride,
		disableOffline = false,
		forceQueue = false,
		...mutationOptions
	} = options;

	const client = useApolloClient();
	const appContext = useAppContext();
	const {
		enqueue,
		isOnline,
		isReady,
		waitUntilReady,
	} = useOfflineQueue();
	const [mutate, mutationState] = useMutation(documentNode, mutationOptions);
	const printedDocument = useMemo(() => print(documentNode), [documentNode]);
	const operationName = useMemo(() => getOperationName(documentNode, options.operationName), [documentNode, options.operationName]);

	const helpers = useMemo(() => ({
		getServerEndpoint: appContext?.getServerEndpoint,
	}), [appContext?.getServerEndpoint]);

	const config = useMemo(() => {
		if (disableOffline) {
			return null;
		}

		if (offlineOverride) {
			return offlineOverride;
		}

		if (!configKey) {
			return null;
		}

		return offlineOperationConfigs[configKey] || null;
	}, [configKey, disableOffline, offlineOverride]);

	const statusRef = useRef({ isReady, isOnline });

	useEffect(() => {
		statusRef.current = { isReady, isOnline };
	}, [isReady, isOnline]);

	const ensureQueueReady = useCallback(async () => {
		if (statusRef.current.isReady) {
			return true;
		}

		if (!waitUntilReady) {
			return false;
		}

		try {
			await waitUntilReady();
			return true;
		}
		catch (error) {
			console.log('waitUntilReady error', error);
			return false;
		}
	}, [waitUntilReady]);

	const executeOfflineMutation = useCallback(async (mutateOptions = {}) => {
		if (!config) {
			return mutate(mutateOptions);
		}

		const currentStatus = statusRef.current;
		const variables = mutateOptions.variables || {};
		const context = mutateOptions.context || {};
		const attachments = config.extractAttachments
			? await config.extractAttachments({ variables, context, client, helpers }) || []
			: [];
		// Device URIs must pass through queue preparation even if connectivity changes before the mutation runs.
		const mustQueueAttachments = Boolean(config.queueWhenAttachments && attachments.length);

		let queueAfterNetworkFailure = false;
		if (currentStatus.isOnline && !mustQueueAttachments && !forceQueue) {
			try {
				return await mutate(mutateOptions);
			}
			catch (error) {
				if (!config.queueOnNetworkError || !isNetworkError(error)) {
					throw error;
				}
				queueAfterNetworkFailure = true;
			}
		}

		let queueReady = currentStatus.isReady;
		if (!queueReady) {
			queueReady = await ensureQueueReady();
		}

		const latestStatus = statusRef.current;
		if (latestStatus.isOnline && !queueAfterNetworkFailure && !mustQueueAttachments && !forceQueue) {
			return mutate(mutateOptions);
		}

		if (!queueReady) {
			if (mustQueueAttachments) {
				throw new Error('Offline queue is not ready');
			}

			return mutate(mutateOptions);
		}

		const queuedEntry = await enqueue({
			configKey,
			document: printedDocument,
			operationName,
			variables: config.serializeVariables
				? await config.serializeVariables({ variables, context, client, helpers })
				: variables,
			context,
			attachments,
			meta: config.createMeta
				? await config.createMeta({ variables, context, client, helpers })
				: {},
		});

		if (config.applyOptimisticUpdate) {
			await config.applyOptimisticUpdate({
				client,
				variables,
				context,
				queueEntry: queuedEntry,
				helpers,
			});
		}

		if (config.onQueued) {
			await config.onQueued({
				client,
				variables,
				context,
				queueEntry: queuedEntry,
				helpers,
			});
		}

		const offlineResult = config.getOfflineResult
			? await config.getOfflineResult({
				client,
				variables,
				context,
				queueEntry: queuedEntry,
				helpers,
			})
			: null;

		return {
			data: offlineResult,
			offline: true,
			queueEntry: queuedEntry,
		};
	}, [config, mutate, enqueue, configKey, printedDocument, operationName, client, helpers, ensureQueueReady, forceQueue]);

	return [executeOfflineMutation, mutationState];
};

export default useOfflineMutation;
